import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { License } from '../models/api/License';
import { Partner } from '../models/api/Partner';
import { AuthenticationService } from './authentication.service';
import { DashboardMetadata } from '../models/api/DashboardMetadata';
import { Discounts } from '../models/api/Discounts';
import { ReplicationStatus } from '../models/replication/ReplicationStatus';
import { ReplicationSettings } from '../models/replication/ReplicationSettings';
import { MigratorStatus } from '../models/replication/MigratorStatus';
import { ReplicationError } from '../models/replication/ReplicationError';
import { PartnerUser } from '../models/api/PartnerUser';
import { PartnerCertifications } from '../models/api/PartnerCertifications';
import { UserRequest } from '../models/api/UserRequest';
import { LicenseEntry } from '../models/api/LicenseEntry';
import { LMUnitResponse } from '../models/api/LMUnitResponse';
import { SubscriptionEntry } from '../models/api/SubscriptionEntry';
import { PaymentCurrency } from '../models/saltpay/PaymentCurrency';
import { History } from '../models/api/History';
import { HttpResponse } from '@angular/common/http';
import { PartnerProgramStatus } from '../models/api/PartnerProgramStatus';
import { PartnerProgram } from '../models/api/PartnerProgram';
import { PartnerProgramAttachments } from '../models/api/PartnerProgramAttachments';
import { LicenseMobileKey } from 'src/app/models/api/LicenseMobileKey';
import { BaseFilter } from '@app/models/api/BaseFilter';
import { PartnerFilter } from '@app/models/api/PartnerFilter';
import { LicenseForSearch } from '@app/models/api/LicenseForSearch';
import { LicenseCoutriesAndProductLines } from '@app/models/api/LicenseCoutriesAndProductLines';
import { ProductLinesCurrenciesAndLicenses } from '@app/models/api/ProductLinesCurrenciesAndLicenses';
import { LMUnitSummaryResponse } from '@app/models/api/LMUnitSummaryResponse';
import { PartnerProgramPage } from '@app/models/api/PartnerProgramPage';
import { PartnerProgramEditableContent } from '@app/models/api/PartnerProgramEditableContent';
import { PartnerProgramLoyaltyPoint } from '@app/models/api/PartnerProgramLoyaltyPoint';
import { PartnerProgramPartnerLevel } from '@app/models/api/PartnerProgramPartnerLevel';
import { PartnerProgramSummary } from '@app/models/api/PartnerProgramSummary';
import { PartnerProgramBreakDown } from '@app/models/api/PartnerProgramBreakDown';
import { PriorPlatform, UpdatePriorPlatformRequest } from '@app/models/api/PriorPlatform';

@Injectable()
export class ApiService {
  constructor(private http: HttpClient, private authService: AuthenticationService) {
    this.partner = new BehaviorSubject<Partner>(null);
    this.partnerUsers = new BehaviorSubject<PartnerUser[]>(null);
    this.partnerUserRequests = new BehaviorSubject<UserRequest[]>(null);
    this.partnerCertifications = new BehaviorSubject<PartnerCertifications>(null);
    this.discounts = new BehaviorSubject<Discounts>(new Discounts());
    this.replicationStatus = new BehaviorSubject<ReplicationStatus>(new ReplicationStatus());
    this.replicationSettings = new BehaviorSubject<ReplicationSettings[]>([]);
    this.migratorStatuses = new BehaviorSubject<MigratorStatus[]>([]);
    this.replicationErrors = new BehaviorSubject<ReplicationError[]>([]);
    this.AutoBillChangeResult = new BehaviorSubject<string>(null);

    this.forceLicensesUpdateMigratorStatus = new BehaviorSubject<MigratorStatus>(null);
    this.licenseValueEntries = new BehaviorSubject<LicenseEntry[]>(null);
    this.lmUnitsResponse = new BehaviorSubject<LMUnitResponse>(null);
    this.licenseMobileValueKeys = new BehaviorSubject<LicenseMobileKey[]>(null);
    this.subscriptionValueEntries = new BehaviorSubject<SubscriptionEntry[]>(null);
    this.history = new BehaviorSubject<History>(null);
  }

  public history: BehaviorSubject<History>;
  public partner: BehaviorSubject<Partner>;
  public partnerUsers: BehaviorSubject<PartnerUser[]>;
  public partnerUserRequests: BehaviorSubject<UserRequest[]>;
  public partnerCertifications: BehaviorSubject<PartnerCertifications>;
  public discounts: BehaviorSubject<Discounts>;
  private discountsUpdated: boolean = false;

  public replicationStatus: BehaviorSubject<ReplicationStatus>;
  public replicationSettings: BehaviorSubject<ReplicationSettings[]>;
  public migratorStatuses: BehaviorSubject<MigratorStatus[]>;
  public forceLicensesUpdateMigratorStatus: BehaviorSubject<MigratorStatus>;
  public replicationErrors: BehaviorSubject<ReplicationError[]>;

  public licenseValueEntries: BehaviorSubject<LicenseEntry[]>;
  public lmUnitsResponse: BehaviorSubject<LMUnitResponse>;
  public licenseMobileValueKeys: BehaviorSubject<LicenseMobileKey[]>;
  public subscriptionValueEntries: BehaviorSubject<SubscriptionEntry[]>;
  public AutoBillChangeResult: BehaviorSubject<string>;
  public selectedLicenseForValue: License = null;

  getLicenses(model: PartnerFilter): Observable<any> {
    return this.http.post<any>('/Licenses/GetForPartner/', model);
  }

  /**
   * Get license by license number
   * @param licenseNo The license number to fetch
   * @returns Observable of License - The license details matching the provided license number.
   */
  getLicenseByNo(licenseNo: string): Observable<License> {
    const url = `/Licenses/GetLicenseByNo/${licenseNo}`;
    return this.http.get<License>(url);
  }

  getAllLicensesForAPartner(): Observable<License[]> {
    const url = `/Licenses/GetAllLicensesForAPartner`;
    return this.http.get<License[]>(url);
  }

  getLicenseCoutriesAndProductLines(isSubscription: number): Observable<LicenseCoutriesAndProductLines> {
    return this.http.post<LicenseCoutriesAndProductLines>('/Licenses/GetLicenseCoutriesAndProductLines/', {
      isSubscription: isSubscription,
    });
  }

  getLicenseMobileKeysCount(LicenseNo: string, isSubscription: boolean): any {
    return this.http.get<any>('/Licenses/MobileLicenses/' + isSubscription + '/' + LicenseNo);
  }

  getLicenseValue(no: string): Observable<LicenseEntry[]> {
    return this.http.post<LicenseEntry[]>('/Licenses/GetLicenseValue/', {
      no: no,
    });
  }

  getLMUnits(licenseKey: string): Observable<LMUnitResponse> {
    return this.http.get<LMUnitResponse>('/Licenses/GetLMUnits?licenseKey=' + licenseKey, {});
  }

  getLMUnitsSummary(licenseKey: string): Observable<LMUnitSummaryResponse> {
    return this.http.get<LMUnitSummaryResponse>('/Licenses/GetLMUnitsSummary?licenseKey=' + licenseKey, {});
  }

  updateLMUnitsCancelationStatus(
    cancelationStatus: number,
    licenseKey: string,
    licenseNo: string,
    units: any[]
  ): Observable<LMUnitResponse> {
    return this.http
      .post<LMUnitResponse>('/Licenses/UpdateLMUnitsCancelationStatus', {
        CancelationStatus: cancelationStatus,
        LicenseKey: licenseKey,
        LicenseNo: licenseNo,
        Units: units,
      })
      .pipe(
        tap((data) => {
          this.lmUnitsResponse.next(data);
        })
      );
  }

  deactivateLMUnits(licenseKey: string, licenseNo: string, erpCodes: number[]): Observable<LMUnitResponse> {
    return this.http
      .post<LMUnitResponse>('/Licenses/DeactivateLMUnits', {
        LicenseKey: licenseKey,
        LicenseNo: licenseNo,
        ErpCodes: erpCodes,
      })
      .pipe(
        tap((data) => {
          this.lmUnitsResponse.next(data);
        })
      );
  }

  getLMSettings(): Observable<any> {
    return this.http.get('/Licenses/GetLMSettings', {});
  }

  saveLMSettings(apiVersion: string): Observable<any> {
    return this.http.post('/Licenses/SaveLMSettings', { apiVersion: apiVersion });
  }

  getLicenseMobileKeys(no: string): Observable<LicenseMobileKey[]> {
    return this.http.post<LicenseMobileKey[]>('/Licenses/GetLicenseMobileKeys/', {
      no: no,
    });
  }

  getPartnerProgramStatuses(): Observable<PartnerProgramStatus[]> {
    return this.http.get<PartnerProgramStatus[]>('/PartnerProgram/codestatusehf/', {});
  }

  getPartnerProgramPages(): Observable<PartnerProgramPage[]> {
    return this.http.get<PartnerProgramPage[]>('/PartnerProgram/GetAllPartnerProgramPages/', {});
  }

  getPartnerProgramEditableContentById(id: any): Observable<PartnerProgramEditableContent> {
    return this.http.get<PartnerProgramEditableContent>('/PartnerProgram/PartnerProgramEditableContentById/' + id, {});
  }

  createPartnerProgramEditableContent(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/createEditableContent/', model);
  }

  updatePartnerProgramEditableContent(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/updateEditableContent/', model);
  }

  deletePartnerProgramEditableContent(id: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/deleteEditableContent/' + id, {});
  }

  getEditableContents(): Observable<any> {
    return this.http.get<any>('/PartnerProgram/GetEditableContents/', {});
  }

  getPartnerProgramLoyaltyPointById(id: any): Observable<PartnerProgramLoyaltyPoint> {
    return this.http.get<PartnerProgramLoyaltyPoint>('/PartnerProgram/PartnerProgramLoyaltyPointById/' + id, {});
  }

  createPartnerProgramLoyaltyPoint(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/createLoyaltyPoint/', model);
  }

  updatePartnerProgramLoyaltyPoint(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/updateLoyaltyPoint/', model);
  }

  deletePartnerProgramLoyaltyPoint(id: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/deleteLoyaltyPoint/' + id, {});
  }

  getLoyaltyPoints(): Observable<any> {
    return this.http.get<any>('/PartnerProgram/GetLoyaltyPoints/', {});
  }

  getLoyaltyPointsByGroup(listGroup: number): Observable<PartnerProgramLoyaltyPoint[]> {
    return this.http.get<PartnerProgramLoyaltyPoint[]>('/PartnerProgram/GetLoyaltyPoints/' + listGroup, {});
  }

  getPartnerProgramPartnerLevelById(id: any): Observable<PartnerProgramPartnerLevel> {
    return this.http.get<PartnerProgramPartnerLevel>('/PartnerProgram/PartnerProgramPartnerLevelById/' + id, {});
  }

  createPartnerProgramPartnerLevel(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/createPartnerLevel/', model);
  }

  updatePartnerProgramPartnerLevel(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/updatePartnerLevel/', model);
  }

  deletePartnerProgramPartnerLevel(id: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/deletePartnerLevel/' + id, {});
  }

  getPartnerLevels(): Observable<any> {
    return this.http.get<any>('/PartnerProgram/GetPartnerLevels/', {});
  }

  getPartnerProgramPageEditableContent(pageId: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/GetPartnerProgramPageEditableContent/' + pageId, {});
  }

  getPartnerProgramSummary(): Observable<PartnerProgramSummary> {
    return this.http.get<PartnerProgramSummary>('/PartnerProgram/GetPartnerProgramSummary/');
  }

  getPartnerProgramBreakDown(): Observable<PartnerProgramBreakDown> {
    return this.http.get<PartnerProgramBreakDown>('/PartnerProgram/GetPartnerProgramBreakDown/');
  }

  getPartnerProgramSummaryHistory(): Observable<PartnerProgramSummary> {
    return this.http.get<PartnerProgramSummary>('/PartnerProgram/GetPartnerProgramSummaryHistory/');
  }

  getPartnerProgramBreakDownHistory(): Observable<PartnerProgramBreakDown> {
    return this.http.get<PartnerProgramBreakDown>('/PartnerProgram/GetPartnerProgramBreakDownHistory/');
  }

  getPartnerProgramAttachments(partnerProgramId: number): Observable<PartnerProgramAttachments[]> {
    return this.http.get<PartnerProgramAttachments[]>(
      '/PartnerProgramAttachment/getbypartnerprogram/' + partnerProgramId,
      {}
    );
  }

  getPartnerProgramLogo(partnerProgramId: number): Observable<PartnerProgramAttachments> {
    return this.http.get<PartnerProgramAttachments>('/PartnerProgram/getPartnerProgramLogo/' + partnerProgramId, {});
  }

  deletePartnerProgramAttachment(attachmentId: any): Observable<any> {
    return this.http.get<any>('/PartnerProgramAttachment/delete/' + attachmentId, {});
  }

  deletePartnerProgramLogo(partnerProgramId: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/deleteLogo/' + partnerProgramId, {});
  }

  downloadPartnerProgramAttachment(attachmentId: any): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>('/PartnerProgramAttachment/download/' + attachmentId, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  downloadPartnerProgramLogo(partnerProgramId: any): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>('/PartnerProgram/downloadLogo/' + partnerProgramId, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  downloadPartnerProgramAttachmentsByProgram(
    programCode: string,
    programStatus: string
  ): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>('/PartnerProgramAttachment/downloadByProgram/' + programCode + '/' + programStatus, {
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  createPartnerProgram(model: any): Observable<any> {
    return this.http.post<any>('/PartnerProgram/create/', model);
  }

  updatePartnerProgram(model: any): Observable<PartnerProgram> {
    return this.http.post<PartnerProgram>('/PartnerProgram/update/', model);
  }

  getPartnerPrograms(model: BaseFilter): Observable<any> {
    return this.http.post<any>('/PartnerProgram/list/', model);
  }

  getPartnerProgramByid(id: any): Observable<PartnerProgram> {
    return this.http.get<PartnerProgram>('/PartnerProgram/' + id, {});
  }

  deletePartnerPrograms(id: any): Observable<any> {
    return this.http.get<any>('/PartnerProgram/delete/' + id, {});
  }

  getSubscriptionValue(no: string): Observable<SubscriptionEntry[]> {
    return this.http.post<SubscriptionEntry[]>('/Licenses/GetSubscriptionValue/', {
      licenseNo: no,
    });
  }

  getDashboardMetadata(): Observable<DashboardMetadata> {
    return this.http.get<DashboardMetadata>('/Licenses/dashboard/');
  }
  getlogs(model: BaseFilter): Observable<any> {
    return this.http.post<any>('/History/', model);
  }
  getLMLogs(model: BaseFilter): Observable<any> {
    return this.http.post<any>('/LMLogs/', model);
  }
  getOrders(model: PartnerFilter): Observable<any> {
    return this.http.post<any>('/Orders/GetForPartner/', model);
  }

  getProductLinesCurrenciesAndLicenses(): Observable<ProductLinesCurrenciesAndLicenses> {
    return this.http.get<ProductLinesCurrenciesAndLicenses>('/Orders/GetProductLinesCurrenciesAndLicenses/');
  }

  public checkUpdatePartner() {
    if (
      this.partner.value == null ||
      this.authService.currentSelectedPartner == null ||
      this.partner.value.customerNo != this.authService.currentSelectedPartner.no
    ) {
      this.getPartner();
    }
  }

  getPartner(): void {
    this.http.get<Partner>('/Partners/GetByNo/').subscribe((data) => {
      this.partner.next(data);
    });
  }

  getPartnerUsers(): void {
    this.http.get<PartnerUser[]>('/Partners/GetUsersWithRoles/').subscribe((data) => {
      this.partnerUsers.next(data);
    });
  }

  getApiLicensesForSearch(model: BaseFilter): Observable<any> {
    return this.http.post<{ totalItems: number; results: LicenseForSearch[] }>(`/Licenses/GetForSearch`, model);
  }

  getUserRequestsForPartner(): void {
    this.http.get<UserRequest[]>('/Partners/GetUserRequests/').subscribe((data) => {
      this.partnerUserRequests.next(data);
    });
  }

  removePartnerUserRequest(id: Number): void {
    var requests = this.partnerUserRequests.value;
    this.partnerUserRequests.next(requests.filter((r) => r.id != id));
  }

  rejectUserRequest(id: Number): Observable<string> {
    return this.http.get('/Partners/RemoveUserRequest/' + id, { responseType: 'text' });
  }

  getPartnerCertifications(): void {
    this.http.get<PartnerCertifications>('/Partners/GetPartnerCertifications/').subscribe((data) => {
      this.partnerCertifications.next(data);
    });
  }

  savePartnerUsers(partnerUsers: PartnerUser[], isDirEnd: boolean): Observable<boolean> {
    return this.http.post<boolean>('/Partners/SaveUsersWithRoles/' + isDirEnd, partnerUsers);
  }

  getDiscounts(): Observable<Discounts> {
    if (!this.discountsUpdated) {
      this.http.get<Discounts>('/Partners/Discounts/').subscribe((data) => {
        this.discounts.next(data);
      });
    }
    return this.discounts;
  }

  updateReplicationDashboard(): void {
    this.http.get<ReplicationStatus>('/EhfReplication/Status').subscribe((data) => {
      this.replicationStatus.next(data);
    });

    this.http.get<ReplicationSettings[]>('/EhfReplication/Settings').subscribe((data) => {
      this.replicationSettings.next(data);
      //console.log(data);
    });

    this.http.get<MigratorStatus[]>('/EhfReplication/StatusList').subscribe((data) => {
      this.migratorStatuses.next(data);
      //console.log(this.migratorStatuses);
    });

    this.http.get<ReplicationError[]>('/EhfReplication/ErrorList').subscribe((data) => {
      this.replicationErrors.next(data);
      //console.log(this.migratorStatuses);
    });
  }

  updateAutoBill(licenseNo, value): void {
    this.http.get('/Licenses/UpdateAutoBill/' + licenseNo + '/' + value, { responseType: 'text' }).subscribe((data) => {
      this.AutoBillChangeResult.next(data);
    });
  }

  forceUpdatePartnerLicenses(no): void {
    this.http.post<MigratorStatus>('/EhfReplication/UpdatePartnerLicenses/', { no: no }).subscribe((data) => {
      this.forceLicensesUpdateMigratorStatus.next(data);
    });
  }

  removeContact(no): Observable<boolean> {
    return this.http.post<boolean>('/Partners/RemoveContact/', { no: no });
  }

  createNewContact(
    email: string,
    firstname: string,
    lastname: string,
    customers: string[],
    roles: string[],
    partnershipRoles: string[],
    requestId: Number,
    isDirEnd: boolean
  ) {
    return this.http.post(
      `/Partners/CreateContact/`,
      {
        email,
        firstname,
        lastname,
        customers,
        roles,
        partnershipRoles,
        requestId,
        isDirEnd,
      },
      { responseType: 'text' }
    );
  }

  downloadInvoices(invoices: string[]): Observable<Blob> {
    return this.http.post(
      `/ReportAsPdf/DownloadInvoices`,
      {
        invoices: invoices,
      },
      {
        responseType: 'blob',
      }
    );
  }

  downloadAccountStatement(
    customerNo: string,
    startDate: string,
    endDate: string,
    isCustomerUS: boolean
  ): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(
      `/ReportAsPdf/DownloadAccountStatement`,
      {
        customerNo,
        startDate,
        endDate,
        isCustomerUS,
      },
      {
        headers: new HttpHeaders({ Timeout: '60000' }),
        observe: 'response',
        responseType: 'blob' as 'json',
      }
    );
  }
  registerSaltPayPayment(payment: PaymentCurrency): Observable<boolean> {
    return this.http.post<boolean>('/Saltpay/register-payment/', payment);
  }

  getNextPaymentId(): Observable<Number> {
    return this.http.get<Number>('/Saltpay/get-next-id/');
  }

  getPaymentsHistory(model: BaseFilter): Observable<any> {
    return this.http.post<any>('/Saltpay/payment-history/', model);
  }

  downloadBalanceToDate(customerNo: string, endDate: string, isCustomerUS: boolean): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(
      `/ReportAsPdf/DownloadBalanceToDate`,
      {
        customerNo,
        endDate,
        isCustomerUS,
      },
      {
        headers: new HttpHeaders({ Timeout: '60000' }),
        observe: 'response',
        responseType: 'blob' as 'json',
      }
    );
  }

  updateCustomerEmail(licenseNo, updatedCustomerEmail): Observable<boolean> {
    return this.http.get<boolean>('/Licenses/UpdateCustomerEmail/' + licenseNo + '/' + updatedCustomerEmail);
  }

  getPriorPlatforms(): Observable<PriorPlatform[]> {
    return this.http.get<PriorPlatform[]>('/Licenses/GetPriorPlatforms');
  }

  updateCustomerLicensePriorPlatform(request: UpdatePriorPlatformRequest): Observable<boolean> {
    return this.http.post<boolean>('/Licenses/UpdateCustomerLicensePriorPlatform', request);
  }

  getAdminPartners(): Observable<any> {
    return this.http.get<any>('/Partners/GetAllPartnerAdminForLoggedUser/');
  }
}
